<script setup lang="ts">
import { PageHeader, PageShell } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  canViewTargets,
  PlanningTabs,
  TargetCreateForm,
  TargetList,
  useTargetAccountLabels,
  useTargets,
} from '@/features/targets'
import { useSession } from '@/app/session'

const { t } = useI18n()
const session = useSession()

const canView = computed(() => canViewTargets(session.can))

const targets = useTargets({
  ledgerId: () => session.activeLedger.value?.id,
  enabled: () => canView.value,
})

const accountLabels = useTargetAccountLabels({
  ledgerId: () => session.activeLedger.value?.id,
  enabled: () => canView.value,
})

const createErrorMessage = computed(() =>
  targets.hasCreateError.value
    ? (targets.createErrorDetail.value ?? t('targets.createErrorBody'))
    : undefined,
)

function createTarget(input: Parameters<typeof targets.create>[0]): void {
  targets.create(input).catch(() => {
    // surfaced through targets.hasCreateError; nothing further to do here
  })
}

function deleteTarget(targetId: string): void {
  targets.remove(targetId).catch(() => {
    // surfaced through targets.hasDeleteError; nothing further to do here
  })
}
</script>

<template>
  <PageShell class="gap-6">
    <PageHeader :title="t('planning.title')">
      <template #subtitle>{{ t('planning.subtitle') }}</template>
    </PageHeader>
    <PlanningTabs />

    <div class="flex items-center justify-between">
      <h2 class="text-[length:var(--text-step-5)] font-semibold text-[color:var(--color-text)]">
        {{ t('targets.title') }}
      </h2>
      <TargetCreateForm
        v-if="canView && session.activeLedger.value !== undefined"
        :ledger-id="session.activeLedger.value.id"
        :is-submitting="targets.isCreating.value"
        :error-message="createErrorMessage"
        @create="createTarget"
      />
    </div>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('targets.noLedger') }}
    </p>

    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">
      {{ t('targets.noAccess') }}
    </p>

    <TargetList
      v-else
      :targets="targets.targets.value"
      :is-loading="targets.isLoading.value"
      :error="targets.error.value"
      :deleting-id="targets.deletingId.value"
      :account-label="accountLabels.labelOf"
      :retrying="targets.retrying.value"
      @retry="targets.retry()"
      @delete="deleteTarget"
    />
  </PageShell>
</template>
